import { enrichableFieldSchema, prospectScopeSchema } from "@amtana/prospect-contract";
import type { EnrichableField } from "@amtana/prospect-contract";
import { z } from "zod";

// AI enrichment fills prospect fields the extraction did not have. Most fields
// cost AI credits per result; account data comes straight from the platform
// and costs normal credits. Already-enriched prospects are skipped unless a
// re-enrich is asked for.

export type EnrichmentSection =
  | "identity"
  | "classification"
  | "locationLanguage"
  | "accountData"
  | "contactDiscovery";

export type EnrichmentFieldInfo = {
  readonly id: EnrichableField;
  readonly label: string;
  readonly description: string;
  readonly section: EnrichmentSection;
  readonly cost: number;
  readonly currency: "ai-credits" | "credits";
};

export const ENRICHMENT_FIELDS: readonly EnrichmentFieldInfo[] = [
  {
    id: "firstName",
    label: "First Name",
    description: "Extract the first name from the prospect's full name",
    section: "identity",
    cost: 1,
    currency: "ai-credits",
  },
  {
    id: "lastName",
    label: "Last Name",
    description: "Extract the last name from the prospect's full name",
    section: "identity",
    cost: 1,
    currency: "ai-credits",
  },
  {
    id: "gender",
    label: "Gender",
    description: "Determine the prospect's gender based on their name",
    section: "identity",
    cost: 1,
    currency: "ai-credits",
  },
  {
    id: "entityType",
    label: "Person / Business",
    description: "Determine if the profile belongs to a person or a business",
    section: "classification",
    cost: 1,
    currency: "ai-credits",
  },
  {
    id: "b2bB2c",
    label: "B2B / B2C",
    description: "Classify whether the prospect is a business (B2B) or consumer (B2C)",
    section: "classification",
    cost: 1,
    currency: "ai-credits",
  },
  {
    id: "niche",
    label: "Niche / Industry",
    description: "Identify the primary niche or industry of the prospect",
    section: "classification",
    cost: 2,
    currency: "ai-credits",
  },
  {
    id: "country",
    label: "Country",
    description: "Identify the prospect's country based on available data",
    section: "locationLanguage",
    cost: 1,
    currency: "ai-credits",
  },
  {
    id: "language",
    label: "Language",
    description: "Detect the prospect's primary language",
    section: "locationLanguage",
    cost: 1,
    currency: "ai-credits",
  },
  {
    id: "accountAge",
    label: "Account Age",
    description: "Month and year the account was created, straight from the platform.",
    section: "accountData",
    cost: 1,
    currency: "credits",
  },
  {
    id: "avgLikes",
    label: "Average Likes",
    description: "Average number of likes across the last 12 posts.",
    section: "accountData",
    cost: 1,
    currency: "credits",
  },
  {
    id: "avgComments",
    label: "Average Comments",
    description: "Average number of comments across the last 12 posts.",
    section: "accountData",
    cost: 1,
    currency: "credits",
  },
  {
    id: "engagementRate",
    label: "Engagement Rate",
    description: "(avg likes + avg comments) / followers × 100.",
    section: "accountData",
    cost: 1,
    currency: "credits",
  },
  {
    id: "websiteContacts",
    label: "Email & Phone from website",
    description:
      "Scrapes the prospect's website to discover email addresses and phone numbers not found during the analysis.",
    section: "contactDiscovery",
    cost: 3,
    currency: "ai-credits",
  },
];

export const ENRICHMENT_PRESETS: ReadonlyArray<{
  id: string;
  label: string;
  fields: readonly EnrichableField[];
}> = [
  { id: "name-extraction", label: "Name Extraction", fields: ["firstName", "lastName"] },
  { id: "classification", label: "Classification", fields: ["gender", "entityType", "b2bB2c", "niche"] },
  {
    id: "full-enrichment",
    label: "Full Enrichment",
    fields: ["firstName", "lastName", "gender", "entityType", "b2bB2c", "niche", "country", "language"],
  },
];

export const enrichmentRequestSchema = z.object({
  scope: prospectScopeSchema,
  fields: z.array(enrichableFieldSchema).min(1, "Select at least one field to enrich"),
  reEnrich: z.boolean().default(false),
});
export type EnrichmentRequest = z.input<typeof enrichmentRequestSchema>;

export const enrichmentPreviewSchema = z.object({
  total: z.number().int().nonnegative(),
  toEnrich: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  aiCreditCost: z.number().int().nonnegative(),
  creditCost: z.number().int().nonnegative(),
  aiCreditBalance: z.number().int().nonnegative(),
  creditBalance: z.number().int().nonnegative().nullable(),
  sufficient: z.boolean(),
});
export type EnrichmentPreview = z.infer<typeof enrichmentPreviewSchema>;

export const runStatusSchema = z.enum(["processing", "completed", "failed", "cancelled"]);
export type RunStatus = z.infer<typeof runStatusSchema>;

export const enrichmentRunSchema = z.object({
  id: z.string(),
  fields: z.array(enrichableFieldSchema),
  status: runStatusSchema,
  total: z.number().int().nonnegative(),
  processed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  aiCreditsUsed: z.number().int().nonnegative(),
  creditsUsed: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
});
export type EnrichmentRun = z.infer<typeof enrichmentRunSchema>;

export const runIdInputSchema = z.object({ runId: z.string() });
